import styled from 'styled-components';
import { GOLD_COLOR, PRIMARY_COLOR, BORDER_COLOR, SECONDARY_TEXT_COLOR } from '../../../constants';
import { BiCoin } from 'react-icons/bi';
import { FaImages } from 'react-icons/fa';
import { useImageGenerationSettingsContext } from '../../../context/ImageGenerationSettingsProvider';
import { SpeedModeType } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { FEED_PATH } from '../../../pathNames';
import { useImagesContext } from '../../../context/ImagesContextProvider';
import { getEngineNameFromModelId } from '../../../utils/engineHelpers';

interface CustomRadioOptionProps {
  name: string;
  handleModeChange: (speedMode: SpeedModeType, modelId: string) => void;
  modelId: string;
  speedModeToSet: SpeedModeType;
  label: string;
  description: string;
  costs?: boolean;
}

const CustomRadioOption = ({
  name,
  handleModeChange,
  modelId,
  speedModeToSet,
  label,
  description,
  costs = false,
}: CustomRadioOptionProps) => {
  const { speedMode, engine } = useImageGenerationSettingsContext();
  const navigate = useNavigate();
  const { setSelectedEngine, setSelectedCategory } = useImagesContext();

  const checked = speedMode === speedModeToSet && engine.modelId === modelId;
  const onChange = () => handleModeChange(speedModeToSet, modelId);

  const handleShowExamples = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent radio selection when clicking the button
    const engineName = getEngineNameFromModelId(modelId);
    setSelectedEngine(engineName);
    setSelectedCategory(undefined);
    navigate(`${FEED_PATH}`);
  };

  return (
    <RadioOption>
      <RadioInput
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <RadioLabelGroup>
        <HeaderRow>
          <RadioLabel>
            {label}
            {costs && <BiCoin color={GOLD_COLOR} />}
          </RadioLabel>
          <ExamplesButton onClick={handleShowExamples}>
            <FaImages size={12} />
            Examples
          </ExamplesButton>
        </HeaderRow>
        <RadioDescription>{description}</RadioDescription>
      </RadioLabelGroup>
    </RadioOption>
  );
};

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const RadioInput = styled.input`
  margin-right: 8px;
  cursor: pointer;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const RadioLabel = styled.span`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ExamplesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  background: transparent;
  border: 1px solid ${BORDER_COLOR};
  border-radius: 4px;
  padding: 2px 6px;
  color: ${SECONDARY_TEXT_COLOR};
  cursor: pointer;

  &:hover {
    border-color: ${PRIMARY_COLOR};
  }
`;

const RadioLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const RadioDescription = styled.span`
  font-size: 0.8rem;
  color: #888;
  cursor: pointer;
`;

export default CustomRadioOption;
